import { AppBar, Avatar, ButtonBase, Toolbar } from '@mui/material';

import { initials, useCurrentUser } from '@/features/account';

import { AccountMenu } from './AccountMenu';
import { HeaderLogo } from './HeaderLogo';
import { useAccountMenu, useAccountSurface } from './useAccountMenu';

export const AppHeader = () => {
  const surface = useAccountSurface();
  const user = useCurrentUser();
  const menu = useAccountMenu();

  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={0}
      // Rounded off at the bottom and lifted on a soft shadow rather than cut
      // off by a hard rule, so the header reads as the same floating, blurred
      // material as the nav dock at the other end of the screen. It stays
      // full-bleed and sticky: the sidebar pins to `top-header` and the notch
      // inset is padded here, so an actually-detached bar would break both.
      // The hairline survives as a ring on three sides, because the translucent
      // surface needs an edge against whatever scrolls beneath it.
      className="rounded-b-2xl bg-surface/85 pt-safe shadow-card ring-1 ring-divider backdrop-blur-md"
    >
      <Toolbar className="min-h-(--spacing-header) gap-2 px-4">
        <HeaderLogo />

        {surface === 'header' && (
          <>
            <ButtonBase
              // Named for the person once known, but never nameless while the
              // profile loads — an unlabelled control is unusable by a screen
              // reader for exactly as long as the request takes.
              aria-label={
                user.isLoaded ? `Account options for ${user.fullName}` : 'Account options'
              }
              {...menu.triggerProps}
              onClick={menu.open}
              // Pushed to the far end of the bar: the logo takes only the
              // width it needs, so without this the avatar trails it instead
              // of sitting under the thumb at the screen edge.
              className="ml-auto size-11 shrink-0 rounded-full"
            >
              {/* The pale panel fill the buttons and card headers carry, so
                  the avatar belongs to the same surface family rather than
                  being the last near-black object in the chrome. Ink is
                  near-black for the same reason it is on a filled button:
                  white on this fill sits at 1.64:1. The header is translucent
                  and blurred, so the shadow is what lifts the disc off
                  whatever scrolls beneath it. */}
              <Avatar className="size-9 bg-primary text-sm font-semibold text-on-primary shadow-card">
                {initials(user.fullName)}
              </Avatar>
            </ButtonBase>

            <AccountMenu
              anchor={menu.anchor}
              id={menu.id}
              onClose={menu.close}
              placement="header"
            />
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};
